// Shared "ask the brain" request: SyQi -> /api/ai/chat, deities -> /api/ai/deity.
// Used by live voice tool calls and by the text chat in TalkingOverlay, so URL + auth
// (apiFetch adds the Bearer token) live in one place.
//
// Screen awareness: every request carries a brief of the screen the user is on
// (screenContext.ts), and deity requests carry the open tool's form values. Changes the
// agent makes land on screen: proposed form fills are applied through the tool's own save
// path, and any record write makes the open tool reload (notifyDataChanged).
import { apiFetch } from '../lib/api';
import { getDeityForm } from '../olmps/deityFormRegistry';
import type { DeityAction } from '../olmps/useSyqiChat';
import { currentFormContext, localDateKey, notifyDataChanged, screenPreamble } from './screenContext';

export async function askAgent(
  toolId: string,
  message: string,
  conversationId?: string,
): Promise<{ text?: string; conversationId?: string; applied: string[] }> {
  const isSyqi = toolId === 'syqi';
  const withScreen = screenPreamble() + message;
  const formContext = isSyqi ? undefined : currentFormContext();
  const today = localDateKey();
  const r = await apiFetch<{ message?: string; reply?: string; conversationId?: string; actions?: DeityAction[]; wrote?: boolean }>(isSyqi ? '/api/ai/chat' : '/api/ai/deity', {
    method: 'POST',
    body: JSON.stringify(isSyqi ? { message: withScreen, conversationId, today } : { toolId, message: withScreen, conversationId, formContext, today }),
  });
  const text = r.message || r.reply;
  const applied: string[] = [];
  let wrote = r.wrote === true;
  for (const a of r.actions ?? []) {
    if (a.kind === 'proposeFormFill') {
      const adapter = getDeityForm(a.formKey);
      if (adapter) {
        adapter.applyFill(a.fields, a.recordId);
        applied.push(a.summary);
      }
    } else if (a.kind === 'wrote') {
      wrote = true;
      applied.push(a.summary);
    }
  }
  // Any record write (deity route actions, or SyQi's `actions` / `wrote`) remounts the open tool so the change shows.
  if (wrote) notifyDataChanged();
  return { text, conversationId: r.conversationId, applied };
}
